import { booleanAttribute, computed, Directive, effect, inject, input } from '@angular/core';
import { useI18n } from '@sona/angular-util-common';
import { ScanRequestId } from '@sona/shared-scan-model-common';
import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { maxBy } from 'lodash';
import { provideMarkdown } from 'ngx-markdown';

import { Issue } from '../../models/issue';
import { BarCellComponent } from '../cells/bar-cell.ng';
import { cellPlaceholder, isPlaceholder } from '../cells/cell-placeholder';
import { SonaImpactCellComponent } from '../cells/impact-cell.ng';
import { IssueTitleCellComponent } from '../cells/issue-title-cell.ng';
import { SonaWcagCategoryCellComponent } from '../cells/wcag-category-cell.ng';
import { SonaWcagLevelCellComponent } from '../cells/wcag-level-cell.ng';

import { SonaDataTableComponent } from './data-table.ng';

const findWcagCategory = (tags: string[]): string | null => {
    const wcagCategory = tags.filter(tag => tag.startsWith('wcag') && tag !== 'wcag2a' && tag !== 'wcag2aa')[0]?.substring(0, 5);

    if (!wcagCategory) {
        return null;
    }

    switch (wcagCategory) {
        case 'wcag1':
            return 'perceivable';
        case 'wcag2':
            return 'operable';
        case 'wcag3':
            return 'understandable';
        case 'wcag4':
            return 'robust';
        default:
            return null;
    }
};

@Directive({
    selector: 'sona-data-table[accessibilityIssuesTable]',
    providers: [
        provideMarkdown()
    ]
})
export class AccessibilityIssuesTableDirective {

    readonly dataTable = inject(SonaDataTableComponent);
    private readonly _i18n = useI18n();

    readonly scanRequestId = input.required<ScanRequestId | undefined>();
    readonly isInDrawer = input(false, { transform: booleanAttribute });

    readonly maxPageCount = computed(() => maxBy(this.dataTable.data(), 'pageCount')?.pageCount ?? 0);
    readonly maxIssueCount = computed(() => maxBy(this.dataTable.data(), 'issueCount')?.issueCount ?? 0);

    readonly columns = computed<ColumnDef<Issue>[]>(() => [
        {
            accessorKey: 'title',
            size: 1600,
            cell: info => isPlaceholder(info.row.original) ? cellPlaceholder() : flexRenderComponent(
                IssueTitleCellComponent,
                {
                    inputs: {
                        issue: info.row.original,
                        scanRequestId: this.scanRequestId(),
                        isInDrawer: this.isInDrawer()
                    }
                }
            ),
            header: () => this._i18n().t('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.COLUMN.ISSUE')
        },
        {
            accessorKey: 'impact',
            size: 144,
            cell: (info) => isPlaceholder(info.row.original) ? cellPlaceholder() : flexRenderComponent(
                SonaImpactCellComponent,
                {
                    inputs: {
                        impact: info.getValue() as string
                    }
                }
            ),
            header: () => this._i18n().t('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.COLUMN.IMPACT')
        },
        {
            id: 'category',
            accessorKey: 'tags',
            size: 144,
            cell: (info) => isPlaceholder(info.row.original) ? cellPlaceholder() : flexRenderComponent(
                SonaWcagCategoryCellComponent,
                {
                    inputs: {
                        category: findWcagCategory(info.getValue() as string[])
                    }
                }
            ),
            header: () => this._i18n().t('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.COLUMN.CATEGORY'),
            enableSorting: false
        },
        {
            id: 'level',
            accessorKey: 'tags',
            size: 144,
            cell: (info) => isPlaceholder(info.row.original) ? cellPlaceholder() : flexRenderComponent(
                SonaWcagLevelCellComponent,
                {
                    inputs: {
                        level: (info.getValue() as string[]).find(tag => tag === 'wcag2a' || tag === 'wcag2aa') ?? null
                    }
                }
            ),
            header: () => this._i18n().t('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.COLUMN.LEVEL'),
            enableSorting: false
        },
        {
            accessorKey: 'pageCount',
            size: 144,
            cell: (info) => isPlaceholder(info.row.original) ? cellPlaceholder({ height: 'xl' }) : flexRenderComponent(
                BarCellComponent,
                {
                    inputs: {
                        value: info.getValue() as number,
                        max: this.maxPageCount()
                    }
                }
            ),
            header: () => this._i18n().t('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.COLUMN.PAGES')
        },
        {
            accessorKey: 'issueCount',
            size: 144,
            cell: (info) => isPlaceholder(info.row.original) ? cellPlaceholder({ height: 'xl' }) : flexRenderComponent(
                BarCellComponent,
                {
                    inputs: {
                        value: info.getValue() as number,
                        max: this.maxIssueCount()
                    }
                }
            ),
            header: () => this._i18n().t('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.COLUMN.COUNT')
        }
    ]);

    constructor() {
        effect(() => {
            this.dataTable.writableColumns.set(this.columns());
            this.dataTable.emptyMessage.set('REPORTS.COMPONENT.TABLE.ACCESSIBILITY_ISSUES.EMPTY_MESSAGE');
        });
    }
}
